import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Search, X, BookOpen, Heart, Sparkles, Moon, Droplet, Apple, Flame, Hand, ChevronRight, Link2, Check } from 'lucide-react';
import { movementResources } from '../data/resources';
import { movementContent } from '../data/movementContent';
import NewsletterSignup from '../components/NewsletterSignup';
import useNewsletterPrompt from '../hooks/useNewsletterPrompt';
import { useResourceDeepLink } from '../hooks/useResourceDeepLink';

// Custom logo for section icons
const SECTION_ICON = 'https://customer-assets.emergentagent.com/job_hello-world-4084/artifacts/vnl8646a_Grounded%20Wellness%20%28Logo%29%20%284%29.png';

// Helper component to render icon - either styled circle or emoji
const SectionIcon = ({ icon, size = 24 }) => {
  if (icon === 'logo') {
    return (
      <span 
        className="inline-block rounded-full bg-terracotta border-2 border-terracotta-dark shadow-sm"
        style={{ 
          width: size * 0.5, 
          height: size * 0.5,
          minWidth: size * 0.5,
          minHeight: size * 0.5
        }}
      />
    );
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

const MovementResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  
  // Newsletter soft prompt
  const { showPrompt, dismissPrompt } = useNewsletterPrompt('movement-resources');

  // Get detailed content if available
  const getDetailedContent = (resourceId) => {
    return movementContent[resourceId] || null;
  };

  // Deep linking
  const { setResourceParam, clearResourceParam, copyResourceLink, copied } = useResourceDeepLink({
    resources: movementResources,
    getDetailedContent,
    onSelect: setSelectedResource,
  });

  const filteredResources = movementResources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeColor = (type) => {
    if (type === 'PDF Guide') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (type === 'Coming Soon') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-sky-100 text-sky-700 border-sky-200';
  };

  const getIconForTip = (title) => {
    const icons = {
      'Topicals': '🧴',
      'Sleep': '🌙',
      'Hydration': '💧',
      'Nutrition': '🥗',
      'Compresses': '🔥',
      'Massage': '👐'
    };
    return icons[title] || '✨';
  };

  const handleResourceClick = (resource) => {
    const detailed = getDetailedContent(resource.id);
    if (detailed) {
      setSelectedResource({ ...resource, detailed });
      setResourceParam(resource.id);
    } else if (resource.downloadUrl) {
      window.open(resource.downloadUrl, '_blank');
    }
  };

  const handleCloseResource = () => {
    setSelectedResource(null);
    clearResourceParam();
  };

  return (
    <div className="bg-cream pt-24 min-h-screen" data-testid="movement-resources-page">
      {/* Header */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/resources"
              className="flex items-center gap-2 text-stone-500 hover:text-forest font-body mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Resources
            </Link>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <h1 className="font-heading text-4xl md:text-5xl text-forest">
                  Movement Resources
                </h1>
                <p className="text-stone-500 font-body mt-2 max-w-xl">
                  Resources for reconnecting with your body through functional movement, breath, and fascial work.
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-stone-300 rounded-full pl-11 pr-4 py-2.5 text-forest text-sm placeholder:text-stone-400 focus:outline-none focus:border-terracotta"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100"
          >
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                I began my career as a fairly traditional personal trainer, but my own recovery from surgery quickly shifted my path. That experience led me into holistic and restorative movement, where healing, strength, and function are rebuilt together, not chased in isolation.
              </p>
              
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                The guidance shared here is not medical advice. Medical care is essential and sometimes absolutely necessary, particularly in acute situations. Medicine often works as a snapshot in time, addressing what is needed in that moment, frequently through symptom management or more invasive measures when systems are failing.
              </p>
              
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                Restorative wellness works at a different level. It focuses on rebuilding strength and function while restoring balance and supporting the body's natural ability to self-regulate and heal. True wellness involves returning the body to homeostasis, where healing becomes an internal process rather than something imposed from the outside.
              </p>
              
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                Strength matters. Weights matter. Supporting the metabolic, endocrine, pain, and tension systems matters. But strength is not built in only one way. Balance, alignment, stability, coordination, and nervous system regulation are equally powerful paths to resilience and recovery.
              </p>
              
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                The longer I work in this field, the clearer it becomes that healing is less about specific exercises and more about relationship. Most of my resources are not exercise programs but invitations, guidance, and inspiration that support a deeper connection with your body and a more intelligent approach to movement.
              </p>
              
              <p className="text-forest font-body leading-relaxed italic">
                Healing is always possible when we begin where we are, not where we think we should be. Explore the resources that resonate with your current experience, and approach them with an open heart and a curious mind.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => {
              const hasDetailedContent = !!getDetailedContent(resource.id);
              return (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => handleResourceClick(resource)}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-all duration-500 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full font-body text-xs border ${getTypeColor(resource.type)}`}>
                          {resource.type}
                        </span>
                      </div>
                      {/* Overlay for clickable items */}
                      {(hasDetailedContent || resource.downloadUrl) && (
                        <div className="absolute inset-0 bg-forest/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="bg-white text-forest px-4 py-2 rounded-full font-body text-sm font-medium">
                            {hasDetailedContent ? 'View Resource' : 'Download Guide'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading text-xl text-forest mb-2 group-hover:text-terracotta transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-stone-500 font-body text-sm mb-4 flex-1">
                        {resource.description}
                      </p>

                      {/* Topics */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {resource.topics.slice(0, 3).map((topic, i) => (
                          <span key={i} className="px-2 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-body">
                            {topic}
                          </span>
                        ))}
                      </div>

                      {/* Action */}
                      <div className="flex items-center gap-2 text-terracotta font-body text-sm font-medium">
                        {hasDetailedContent ? (
                          <>
                            <BookOpen size={16} />
                            Read Resource
                            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        ) : resource.downloadUrl ? (
                          <>
                            <Download size={16} />
                            Download Guide
                          </>
                        ) : resource.type === 'Coming Soon' ? (
                          <span className="text-stone-400 italic">Coming soon...</span>
                        ) : (
                          <span className="text-stone-400">Content in development</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-16">
              <p className="text-stone-500 font-body">No resources found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Resource Detail Modal */}
      <AnimatePresence>
        {selectedResource?.detailed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={handleCloseResource}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-cream rounded-2xl max-w-3xl w-full my-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedResource.detailed.image}
                  alt={selectedResource.detailed.title}
                  className="w-full h-56 object-cover rounded-t-2xl"
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => copyResourceLink(selectedResource.id)}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    title="Copy shareable link"
                    data-testid="copy-resource-link"
                  >
                    {copied ? <Check size={20} className="text-emerald-600" /> : <Link2 size={20} className="text-forest" />}
                  </button>
                  <button
                    onClick={handleCloseResource}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    data-testid="close-resource-modal"
                  >
                    <X size={20} className="text-forest" />
                  </button>
                </div>
                {copied && (
                  <div className="absolute top-16 right-4 bg-emerald-600 text-white text-xs font-body px-3 py-1 rounded-full shadow-lg">
                    Link copied!
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-forest/80 to-transparent p-6">
                  <p className="text-cream/80 font-body text-sm mb-1">{selectedResource.detailed.subtitle}</p>
                  <h2 className="font-heading text-3xl text-white">
                    {selectedResource.detailed.title}
                  </h2>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8 space-y-8">
                {/* Introduction */}
                <div>
                  <p className="text-stone-600 font-body leading-relaxed">
                    {selectedResource.detailed.introduction}
                  </p>
                  {selectedResource.detailed.keyInsight && (
                    <div className="mt-4 bg-terracotta/10 border-l-4 border-terracotta p-4 rounded-r-lg">
                      <p className="text-forest font-body italic">
                        "{selectedResource.detailed.keyInsight}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Main Sections */}
                {selectedResource.detailed.sections?.map((section, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <SectionIcon icon={section.icon} size={24} />
                      <div>
                        <h3 className="font-heading text-xl text-forest">{section.title}</h3>
                        {section.subtitle && (
                          <p className="text-stone-500 font-body text-sm">{section.subtitle}</p>
                        )}
                      </div>
                    </div>
                    
                    {section.content && (
                      <p className="text-stone-600 font-body mb-4">{section.content}</p>
                    )}
                    
                    {section.items && (
                      <ul className="space-y-3">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="w-2 h-2 bg-terracotta rounded-full mt-2 flex-shrink-0" />
                            <div>
                              <span className="font-body font-semibold text-forest">{item.term}</span>
                              <span className="text-stone-600 font-body"> — {item.definition}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.subsections && (
                      <div className="mt-4 space-y-3">
                        {section.subsections.map((sub, i) => (
                          <div key={i} className="bg-stone-50 rounded-lg p-4">
                            <span className="font-body font-semibold text-forest">{sub.type}:</span>
                            <span className="text-stone-600 font-body"> {sub.tip}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Practical Tips */}
                {selectedResource.detailed.practicalTips && (
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-4 flex items-center gap-2">
                      <Sparkles className="text-terracotta" size={24} />
                      Practical Tips
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedResource.detailed.practicalTips.map((tip, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <SectionIcon icon={tip.icon} size={20} />
                            <h4 className="font-heading text-lg text-forest">{tip.title}</h4>
                          </div>
                          <ul className="space-y-2">
                            {tip.tips.map((t, i) => (
                              <li key={i} className="flex gap-2 text-stone-600 font-body text-sm">
                                <span className="text-terracotta">•</span>
                                {t}
                              </li>
                            ))}
                          </ul>
                          {tip.note && (
                            <p className="mt-3 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg text-xs font-body">
                              ⚠️ {tip.note}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Complementary Healing */}
                {selectedResource.detailed.complementaryHealing && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.complementaryHealing.icon} size={20} />
                      <h3 className="font-heading text-xl text-forest">
                        {selectedResource.detailed.complementaryHealing.title}
                      </h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">
                      {selectedResource.detailed.complementaryHealing.intro}
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-body font-semibold text-forest text-sm mb-2">Regulated Practitioners:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedResource.detailed.complementaryHealing.regulated.map((p, i) => (
                            <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-body">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="font-body font-semibold text-forest text-sm mb-2">Alternative Therapies:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedResource.detailed.complementaryHealing.alternative.map((p, i) => (
                            <span key={i} className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-xs font-body">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mind-Body Section */}
                {selectedResource.detailed.mindBody && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.mindBody.icon} size={20} />
                      <h3 className="font-heading text-2xl text-forest">
                        {selectedResource.detailed.mindBody.title}
                      </h3>
                    </div>
                    <p className="text-stone-600 font-body mb-6">
                      {selectedResource.detailed.mindBody.intro}
                    </p>
                    <div className="space-y-4">
                      {selectedResource.detailed.mindBody.practices.map((practice, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-terracotta">
                          <h4 className="font-heading text-lg text-forest mb-2">{practice.title}</h4>
                          <p className="text-stone-600 font-body text-sm">{practice.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ocean Analogy (Bodies Like Water) */}
                {selectedResource.detailed.oceanAnalogy && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.oceanAnalogy.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.oceanAnalogy.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.oceanAnalogy.intro}</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.oceanAnalogy.points.map((point, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{point}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-terracotta/10 rounded-lg p-4">
                      <p className="font-heading text-lg text-forest italic text-center">"{selectedResource.detailed.oceanAnalogy.insight}"</p>
                    </div>
                  </div>
                )}

                {/* Applying to Body */}
                {selectedResource.detailed.applyingToBody && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.applyingToBody.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.applyingToBody.title}</h3>
                    </div>
                    <p className="text-cream font-heading text-lg italic mb-4">"{selectedResource.detailed.applyingToBody.content}"</p>
                    <p className="text-cream/90 font-body mb-3">{selectedResource.detailed.applyingToBody.healing}</p>
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.applyingToBody.fluidity}</p>
                    <p className="text-terracotta font-heading italic text-center">"{selectedResource.detailed.applyingToBody.keyQuote}"</p>
                  </div>
                )}

                {/* Dealing with Pain */}
                {selectedResource.detailed.dealingWithPain && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.dealingWithPain.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.dealingWithPain.title}</h3>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.dealingWithPain.points.map((point, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{point}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-stone-100 rounded-lg p-4 mb-3">
                      <p className="text-stone-700 font-body text-sm">{selectedResource.detailed.dealingWithPain.reality}</p>
                    </div>
                    <p className="text-forest font-body italic text-sm">{selectedResource.detailed.dealingWithPain.rareSkill}</p>
                  </div>
                )}

                {/* Body Shaming */}
                {selectedResource.detailed.bodyShaming && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bodyShaming.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodyShaming.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.bodyShaming.intro}</p>
                    <p className="text-stone-500 font-body text-sm italic mb-4">{selectedResource.detailed.bodyShaming.humor}</p>
                    <div className="bg-white rounded-lg p-4 mb-4">
                      {selectedResource.detailed.bodyShaming.dialogue.map((line, idx) => (
                        <p key={idx} className="text-stone-600 font-body text-sm italic">{line}</p>
                      ))}
                    </div>
                    <p className="text-forest font-body text-sm">{selectedResource.detailed.bodyShaming.truth}</p>
                  </div>
                )}

                {/* Positive Relationship */}
                {selectedResource.detailed.positiveRelationship && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.positiveRelationship.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.positiveRelationship.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.positiveRelationship.intro}</p>
                    <div className="space-y-3 mb-4">
                      {selectedResource.detailed.positiveRelationship.gratitudes.map((gratitude, idx) => (
                        <p key={idx} className="text-cream font-body italic text-sm border-l-2 border-terracotta pl-3">"{gratitude}"</p>
                      ))}
                    </div>
                    <p className="text-cream/80 font-body text-sm">{selectedResource.detailed.positiveRelationship.shift}</p>
                  </div>
                )}

                {/* True Healing */}
                {selectedResource.detailed.trueHealing && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.trueHealing.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.trueHealing.title}</h3>
                    </div>
                    <div className="bg-terracotta/10 rounded-lg p-4 mb-4">
                      <p className="font-heading text-lg text-forest italic text-center">"{selectedResource.detailed.trueHealing.content}"</p>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.trueHealing.design}</p>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.trueHealing.mindTricks}</p>
                  </div>
                )}

                {/* Practical Guidance */}
                {selectedResource.detailed.practicalGuidance && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.practicalGuidance.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.practicalGuidance.title}</h3>
                    </div>
                    <div className="space-y-4">
                      {selectedResource.detailed.practicalGuidance.steps.map((step, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-8 h-8 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <h4 className="font-heading text-forest mb-1">{step.title}</h4>
                            <p className="text-stone-600 font-body text-sm">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Takeaways */}
                {selectedResource.detailed.keyTakeaways && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.keyTakeaways.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.keyTakeaways.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {selectedResource.detailed.keyTakeaways.points.map((point, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">✓</span>{point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Closing Quote */}
                {selectedResource.detailed.closingQuote && (
                  <div className="bg-forest text-cream rounded-xl p-6 text-center">
                    <p className="font-heading text-xl italic">"{selectedResource.detailed.closingQuote}"</p>
                  </div>
                )}

                {/* Nature of Etheric (Etheric Body) */}
                {selectedResource.detailed.natureOfEtheric && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.natureOfEtheric.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.natureOfEtheric.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.natureOfEtheric.intro}</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.natureOfEtheric.points.map((point, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{point}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-terracotta/10 rounded-lg p-4">
                      <p className="text-forest font-body text-sm">{selectedResource.detailed.natureOfEtheric.practices}</p>
                    </div>
                  </div>
                )}

                {/* Origins of Word */}
                {selectedResource.detailed.originsOfWord && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.originsOfWord.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.originsOfWord.title}</h3>
                    </div>
                    <p className="text-cream font-heading text-lg italic mb-3">"{selectedResource.detailed.originsOfWord.content}"</p>
                    <p className="text-cream/90 font-body mb-3">{selectedResource.detailed.originsOfWord.cosmology}</p>
                    <p className="text-cream/80 font-body text-sm">{selectedResource.detailed.originsOfWord.adoption}</p>
                  </div>
                )}

                {/* Esoteric Traditions */}
                {selectedResource.detailed.esotericTraditions && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.esotericTraditions.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.esotericTraditions.title}</h3>
                    </div>
                    <div className="space-y-4 mb-4">
                      {selectedResource.detailed.esotericTraditions.traditions.map((tradition, idx) => (
                        <div key={idx} className="border-l-4 border-terracotta pl-4">
                          <h4 className="font-heading text-forest mb-1">{tradition.name}</h4>
                          <p className="text-stone-600 font-body text-sm">{tradition.description}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-forest font-body italic text-sm">{selectedResource.detailed.esotericTraditions.insight}</p>
                  </div>
                )}

                {/* Energy Field */}
                {selectedResource.detailed.energyField && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.energyField.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.energyField.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.energyField.intro}</p>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.energyField.function}</p>
                    <p className="text-forest font-body font-medium">{selectedResource.detailed.energyField.benefits}</p>
                  </div>
                )}

                {/* Living Blueprint */}
                {selectedResource.detailed.livingBlueprint && (
                  <div className="bg-forest text-cream rounded-xl p-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.livingBlueprint.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.livingBlueprint.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-3">{selectedResource.detailed.livingBlueprint.content}</p>
                    <p className="text-cream font-heading italic text-lg">"{selectedResource.detailed.livingBlueprint.harmony}"</p>
                  </div>
                )}

                {/* Aura Layers */}
                {selectedResource.detailed.auraLayers && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.auraLayers.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.auraLayers.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.auraLayers.intro}</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.auraLayers.layers.map((layer, idx) => (
                        <div key={idx} className="bg-gradient-to-r from-violet-50 to-terracotta/10 rounded-lg p-3 border-l-4 border-violet-400">
                          <p className="font-heading text-forest text-sm">{layer.name}</p>
                          <p className="text-stone-500 font-body text-xs">{layer.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Constriction Expansion (Etheric Body) */}
                {selectedResource.detailed.constrictionExpansion && selectedResource.detailed.constrictionExpansion.contractedStances && (
                  <div className="bg-stone-100 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.constrictionExpansion.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.constrictionExpansion.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.constrictionExpansion.intro}</p>
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <p className="font-body text-stone-500 text-sm mb-2">Contracted stances that tighten our field:</p>
                      <ul className="space-y-1">
                        {selectedResource.detailed.constrictionExpansion.contractedStances.map((stance, idx) => (
                          <li key={idx} className="text-stone-600 font-body text-sm italic">"{stance}"</li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.constrictionExpansion.impact}</p>
                    <div className="bg-terracotta/20 rounded-lg p-4">
                      <p className="text-forest font-body italic text-sm">{selectedResource.detailed.constrictionExpansion.painTruth}</p>
                    </div>
                  </div>
                )}

                {/* Expansion Practice */}
                {selectedResource.detailed.expansionPractice && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.expansionPractice.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.expansionPractice.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.expansionPractice.intro}</p>
                    <p className="font-body text-stone-500 text-sm mb-2">Expansion shows up when:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.expansionPractice.showsUpWhen.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">✓</span>{item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-forest font-heading italic text-center">"{selectedResource.detailed.expansionPractice.key}"</p>
                  </div>
                )}

                {/* Simplest Truth (Etheric) */}
                {selectedResource.detailed.simplestTruth && selectedResource.detailed.simplestTruth.friendQuestion && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.simplestTruth.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.simplestTruth.title}</h3>
                    </div>
                    <p className="text-cream font-heading text-xl italic mb-4 text-center">"{selectedResource.detailed.simplestTruth.content}"</p>
                    <p className="text-cream/80 font-body text-sm">{selectedResource.detailed.simplestTruth.friendQuestion}</p>
                  </div>
                )}

                {/* Best Friend (Etheric) */}
                {selectedResource.detailed.bestFriend && selectedResource.detailed.bestFriend.means && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bestFriend.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bestFriend.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.bestFriend.intro}</p>
                    <p className="font-body text-stone-500 text-sm mb-2">Non-abandonment means:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.bestFriend.means.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{item}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-terracotta/10 rounded-lg p-4">
                      <p className="text-forest font-heading italic text-center">"{selectedResource.detailed.bestFriend.key}"</p>
                    </div>
                  </div>
                )}

                {/* Quotes */}
                {selectedResource.detailed.quotes && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    {selectedResource.detailed.quotes.map((quote, idx) => (
                      <p key={idx} className={`font-heading text-lg italic ${idx > 0 ? 'mt-4 pt-4 border-t border-cream/20' : ''}`}>
                        "{quote}"
                      </p>
                    ))}
                  </div>
                )}

                {/* Recommended Books */}
                {selectedResource.detailed.books && (
                  <div>
                    <h3 className="font-heading text-xl text-forest mb-4 flex items-center gap-2">
                      <BookOpen className="text-terracotta" size={20} />
                      Recommended Reading
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.books.map((book, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex items-center gap-3">
                          <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center text-lg">
                            📚
                          </div>
                          <div>
                            <p className="font-body font-semibold text-forest text-sm">{book.title}</p>
                            <p className="text-stone-500 font-body text-xs">{book.author}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Body Never Lies */}
                {selectedResource.detailed.bodyNeverLies && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodyNeverLies.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodyNeverLies.title}</h3>
                    </div>
                    {selectedResource.detailed.bodyNeverLies.paragraphs.map((p, idx) => (
                      <p key={idx} className="text-stone-600 font-body leading-relaxed">{p}</p>
                    ))}
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.bodyNeverLies.insight}</p>
                    </div>
                  </div>
                )}

                {/* Body Signals */}
                {selectedResource.detailed.bodySignals && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodySignals.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodySignals.title}</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {selectedResource.detailed.bodySignals.signals.map((s, idx) => (
                        <div key={idx} className="bg-forest/5 rounded-xl p-3">
                          <p className="font-body text-forest text-sm font-medium">{s.signal}</p>
                          <p className="text-stone-500 font-body text-xs">{s.meaning}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-600 font-body italic text-center">{selectedResource.detailed.bodySignals.insight}</p>
                  </div>
                )}

                {/* Symptoms as Messages */}
                {selectedResource.detailed.symptomsAsMessages && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.symptomsAsMessages.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.symptomsAsMessages.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.symptomsAsMessages.content}</p>
                    <div className="space-y-2">
                      {selectedResource.detailed.symptomsAsMessages.questions.map((q, i) => (
                        <div key={i} className="bg-forest/5 rounded-xl p-3 flex items-center gap-3">
                          <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{i+1}</span>
                          <p className="text-forest font-body text-sm italic">{q}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.symptomsAsMessages.note}</p>
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.symptomsAsMessages.insight}</p>
                    </div>
                  </div>
                )}

                {/* Body Map */}
                {selectedResource.detailed.bodyMap && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodyMap.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodyMap.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.bodyMap.intro}</p>
                    <div className="grid gap-2">
                      {selectedResource.detailed.bodyMap.areas.map((b, i) => (
                        <div key={i} className="flex items-start gap-3 bg-forest/5 rounded-xl p-3">
                          <span className="bg-forest text-cream text-xs font-heading px-2 py-1 rounded-full flex-shrink-0 min-w-[90px] text-center">{b.area}</span>
                          <p className="text-stone-600 font-body text-sm">{b.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Closing Thought */}
                {selectedResource.detailed.closingThought && (
                  <div className="bg-terracotta/10 rounded-xl p-6 text-center">
                    <p className="text-forest font-body whitespace-pre-line">
                      {selectedResource.detailed.closingThought}
                    </p>
                  </div>
                )}

                {/* Breath Exercise */}
                {selectedResource.detailed.breathExercise && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.breathExercise.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.breathExercise.title}</h3>
                    </div>
                    <p className="text-cream/90 font-body mb-4">{selectedResource.detailed.breathExercise.intro}</p>
                    <div className="space-y-3 mb-4">
                      {selectedResource.detailed.breathExercise.steps.map((step, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div className="w-6 h-6 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-body font-bold text-xs">
                            {idx + 1}
                          </div>
                          <p className="text-cream/90 font-body text-sm">{step}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-cream/10 rounded-lg p-4 mt-4">
                      <p className="text-terracotta font-heading italic text-center">"{selectedResource.detailed.breathExercise.insight}"</p>
                    </div>
                  </div>
                )}

                {/* Reawakening */}
                {selectedResource.detailed.reawakening && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.reawakening.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.reawakening.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.reawakening.content}</p>
                    <div className="bg-terracotta/10 rounded-lg p-4">
                      <p className="font-heading text-lg text-forest italic text-center">"{selectedResource.detailed.reawakening.keyInsight}"</p>
                    </div>
                  </div>
                )}

                {/* Download Option */}
                {selectedResource.downloadUrl && (
                  <div className="pt-4 border-t border-stone-200">
                    <a
                      href={selectedResource.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-all"
                    >
                      <Download size={18} />
                      Download Original PDF
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Newsletter Soft Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <NewsletterSignup 
            variant="prompt" 
            onClose={dismissPrompt} 
            source="movement-resources" 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MovementResourcesPage;
